import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/podkit/Card";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Pill } from "@/components/podkit/status/Pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import { SortableTableHead, type TableSortOrder } from "@/components/podkit/tables/SortableTable";
import { toast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { MemberIcon } from "@/components/settings/organization/members/MemberIcon";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { TrackLocations } from "@/hooks/use-segment";
import {
    roleName,
    useLeaveOrganization,
    useSetRole,
    type PlainOrganizationMember,
} from "@/queries/organization-queries";
import { useDeleteUser, useSuspendUser } from "@/queries/user-queries";
import { formatError } from "@/utils/errors";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import { DialogClose } from "@radix-ui/react-dialog";
import type { AccountMembership } from "gitpod-next-api/gitpod/v1/account_pb";
import { OrganizationRole, type OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { User } from "gitpod-next-api/gitpod/v1/user_pb";
import { UserStatus } from "gitpod-next-api/gitpod/v1/user_pb";
import { useCallback, useContext, useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

export type MembersListProps = {
    user?: PlainMessage<User>;
    membership?: PlainMessage<AccountMembership>;
    data: {
        members: PlainMessage<OrganizationMember>[];
    };
    onInviteMembers?: () => void;
};

type SortColumn = "dateJoined" | "role" | "authenticatedWith";

const getLoginProviderDisplayName = (provider: string): string => {
    if (!provider) return "Unknown";

    switch (provider) {
        case "magiclink":
            return "Email";
        case "github":
            return "GitHub";
        case "gitlab":
            return "GitLab";
        case "google":
            return "Google";
        case "custom":
            return "SSO";
        default:
            return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
};

const sortComparators: Record<
    SortColumn,
    (a: PlainOrganizationMember, b: PlainOrganizationMember) => number
> = {
    dateJoined: (a, b) =>
        new Timestamp(a.memberSince).toDate().getTime() - new Timestamp(b.memberSince).toDate().getTime(),
    role: (a, b) => a.role - b.role,
    authenticatedWith: (a, b) =>
        getLoginProviderDisplayName(a.loginProvider).localeCompare(getLoginProviderDisplayName(b.loginProvider)),
};

export const MembersList: FC<MembersListProps> = ({ user, membership, data, onInviteMembers }) => {
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);
    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const [sortColumn, setSortColumn] = useState<SortColumn | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<TableSortOrder>("asc");

    const handleSort = useCallback((columnName: string, order: TableSortOrder) => {
        setSortColumn(columnName as SortColumn);
        setSortOrder(order);
    }, []);

    const sortedMembers = useMemo(() => {
        if (!data.members || !user) return [];

        return [...data.members].sort((a, b) => {
            if (a.userId === user.id) return -1;
            if (b.userId === user.id) return 1;

            if (sortColumn) {
                const comparison = sortComparators[sortColumn](a, b);
                return sortOrder === "asc" ? comparison : -comparison;
            }

            const aFirstName = a.fullName.split(" ")[0];
            const bFirstName = b.fullName.split(" ")[0];
            return aFirstName.localeCompare(bFirstName);
        });
    }, [data.members, user, sortColumn, sortOrder]);

    useEffect(() => {
        if (isGroupManagementEnabled) {
            // TODO(at) remove the Invite Members button contribution when we remove the feature flag
            return;
        }
        if (!isAdmin || !onInviteMembers) {
            return;
        }

        setBreadCrumbRowAction(
            <Button size="sm" onClick={onInviteMembers} data-tracking-id="invite-members-members-list">
                Invite Members
            </Button>,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
    }, [isAdmin, onInviteMembers, setBreadCrumbRowAction, isGroupManagementEnabled]);

    if (!data.members || !user || !membership) {
        return <LoadingState />;
    }

    return (
        <>
            <Table data-testid="members-table" className="mt-4">
                <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                    <TableRow className="h-8 text-base">
                        <TableHead className="pl-3">Name</TableHead>
                        <SortableTableHead
                            className="pl-3"
                            columnName="dateJoined"
                            sortOrder={sortColumn === "dateJoined" ? sortOrder : undefined}
                            onSort={handleSort}
                        >
                            Date joined
                        </SortableTableHead>
                        <SortableTableHead
                            className="pl-3"
                            columnName="role"
                            sortOrder={sortColumn === "role" ? sortOrder : undefined}
                            onSort={handleSort}
                        >
                            Role
                        </SortableTableHead>
                        <SortableTableHead
                            className="pl-3"
                            columnName="authenticatedWith"
                            sortOrder={sortColumn === "authenticatedWith" ? sortOrder : undefined}
                            onSort={handleSort}
                        >
                            Authenticated with
                        </SortableTableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody className="border-t border-border-light text-base">
                    {sortedMembers.map((member) => (
                        <MemberItem key={member.userId} user={user} membership={membership} member={member} />
                    ))}
                </TableBody>
            </Table>
        </>
    );
};

type MemberItemProps = {
    user: PlainMessage<User>;
    membership: PlainMessage<AccountMembership>;
    member: PlainOrganizationMember;
};

const MemberItem: FC<MemberItemProps> = ({ user, membership, member }: MemberItemProps) => {
    const navigate = useNavigate();

    const leaveOrganization = useLeaveOrganization();
    const suspendUser = useSuspendUser();
    const deleteUser = useDeleteUser();
    const setRole = useSetRole();

    const [showModal, setShowModal] = useState<"suspend" | "change-role" | "leave" | "delete" | undefined>(undefined);

    const isModalShown = showModal !== undefined;

    const isMemberCurrentUser = user.id === member.userId;

    const isAdmin = membership.userRole == OrganizationRole.ADMIN;

    const memberHasLeft = member.status === UserStatus.LEFT;
    const memberIsSuspended = member.status === UserStatus.SUSPENDED;

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(member.userId);
        toast({
            title: `Member ID copied to clipboard`,
            description: member.userId,
        });
    }, [member.userId]);

    const memberSince = useMemo(
        () => new Timestamp(member.memberSince).toDate().toLocaleDateString(),
        [member.memberSince],
    );

    const handleViewEnvironments = useCallback(() => {
        navigate(`/settings/environments?creator=${member.userId}`);
    }, [navigate, member.userId]);

    const handleUnsuspendUser = useCallback(async () => {
        await suspendUser.mutateAsync(
            { userId: member.userId, suspended: false },
            {
                onError: (e) => {
                    toast({ title: "Failed to unsuspend user", description: formatError(e) });
                },
            },
        );
    }, [member.userId, suspendUser]);

    const loginProvider = useMemo(() => getLoginProviderDisplayName(member.loginProvider), [member.loginProvider]);

    const avatar = useCallback((m: PlainOrganizationMember) => {
        if (m.avatarUrl) {
            return (
                <img loading="lazy" referrerPolicy="no-referrer" src={m.avatarUrl} className="h-8 w-8 rounded-full" />
            );
        }

        return <MemberIcon name={m.fullName} />;
    }, []);

    return (
        <>
            <TableRow className="h-16 border-border-light">
                <TableCell className="max-w-xs">
                    <div className="inline-flex grow items-center space-x-3">
                        {avatar(member)}
                        <div className="flex flex-col">
                            <Text className="inline-flex items-center gap-2 text-base font-bold">
                                <span className={cn({ "line-through": memberHasLeft || memberIsSuspended })}>
                                    {member.fullName}
                                </span>
                                {memberIsSuspended && (
                                    <Pill
                                        variant="neutral"
                                        size={"md"}
                                        tooltip="This user has been suspended and will not be able to re-join the organization"
                                    >
                                        Suspended
                                    </Pill>
                                )}
                                {memberHasLeft && (
                                    <Pill
                                        tooltip="This user has left the organization and can re-join at any time"
                                        variant="neutral"
                                        size="md"
                                    >
                                        Left
                                    </Pill>
                                )}
                                {isMemberCurrentUser && <Pill variant="success">me</Pill>}
                            </Text>
                            <Text className={`text-base text-content-secondary`}>{member.email}</Text>
                        </div>
                    </div>
                </TableCell>
                <TableCell className="w-40">
                    <Text className={`text-base`}>{memberSince}</Text>
                </TableCell>
                <TableCell className="w-40">
                    <Text className={`text-base`}>{roleName(member.role)}</Text>
                </TableCell>
                <TableCell className="w-40">
                    <Text className={`text-base`}>{loginProvider}</Text>
                </TableCell>
                <TableCell className="w-8">
                    <div className="flex flex-row items-center">
                        <DropdownActions triggerTestId={`user-actions-dropdown-trigger-${member.userId}`}>
                            {isAdmin && (
                                <DropdownMenuItem
                                    disabled={isModalShown}
                                    onClick={handleViewEnvironments}
                                    data-tracking-id="view-member-environments-members-list"
                                >
                                    View Environments
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={handleCopyId} data-tracking-id="copy-member-id-members-list">
                                Copy ID
                            </DropdownMenuItem>
                            {isAdmin && (
                                <DropdownMenuItem
                                    disabled={isModalShown}
                                    onClick={() => setShowModal("change-role")}
                                    data-tracking-id="change-member-role-members-list"
                                >
                                    Change Role
                                </DropdownMenuItem>
                            )}
                            {isMemberCurrentUser && (
                                <DropdownMenuItem
                                    disabled={isModalShown}
                                    onClick={() => setShowModal("leave")}
                                    data-tracking-id="leave-organization-members-list"
                                >
                                    Leave
                                </DropdownMenuItem>
                            )}
                            {isAdmin && !memberIsSuspended && !isMemberCurrentUser && (
                                <DropdownMenuItem
                                    disabled={isModalShown}
                                    onClick={() => setShowModal("suspend")}
                                    className="text-red-500"
                                    data-testid="user-action-suspend"
                                    data-tracking-id="suspend-member-members-list"
                                >
                                    Suspend
                                </DropdownMenuItem>
                            )}
                            {isAdmin && memberIsSuspended && !isMemberCurrentUser && (
                                <DropdownMenuItem
                                    disabled={isModalShown}
                                    onClick={handleUnsuspendUser}
                                    data-testid="user-action-unsuspend"
                                    data-tracking-id="unsuspend-member-members-list"
                                >
                                    Unsuspend
                                </DropdownMenuItem>
                            )}
                            {isAdmin && !isMemberCurrentUser && (
                                <DropdownMenuItem
                                    disabled={isModalShown}
                                    onClick={() => setShowModal("delete")}
                                    className="text-red-500"
                                    data-testid="user-action-delete"
                                    data-tracking-id="delete-member-members-list"
                                >
                                    Delete
                                </DropdownMenuItem>
                            )}
                        </DropdownActions>
                    </div>
                </TableCell>
                {showModal === "change-role" && (
                    <ChangeRoleModal
                        member={member}
                        onContinue={(role) => {
                            setShowModal(undefined);
                            setRole.mutate(
                                { userID: member.userId, role: role },
                                {
                                    onSuccess: () => {
                                        toast({
                                            title: `Changed role for user ${member.fullName} to ${roleName(role)}`,
                                        });
                                    },
                                    onError: (e) => {
                                        toast({ title: `Failed to change role`, description: formatError(e) });
                                    },
                                },
                            );
                        }}
                        onClose={() => {
                            setShowModal(undefined);
                        }}
                    />
                )}
                {showModal === "suspend" && (
                    <SuspendMemberModal
                        member={member}
                        onContinue={() => {
                            setShowModal(undefined);
                            suspendUser.mutate(
                                { userId: member.userId, suspended: true },
                                {
                                    onSuccess: () => {
                                        toast({ title: `User suspended` });
                                    },
                                    onError: (e) => {
                                        toast({ title: `Failed to suspend user`, description: formatError(e) });
                                    },
                                },
                            );
                        }}
                        onClose={() => {
                            setShowModal(undefined);
                        }}
                    />
                )}
                {showModal === "leave" && (
                    <LeaveOrganizationModal
                        onContinue={() => {
                            setShowModal(undefined);
                            leaveOrganization.mutate(
                                { userId: member.userId },
                                {
                                    onSuccess: () => {
                                        // TODO(at) check if this really works as intended
                                        navigate("/", { replace: true });
                                        toast({ title: `You've left the organization` });
                                    },
                                    onError: (e) => {
                                        toast({
                                            title: `Failed to leave the organization`,
                                            description: formatError(e),
                                        });
                                    },
                                },
                            );
                        }}
                        onClose={() => {
                            setShowModal(undefined);
                        }}
                    />
                )}
                {showModal === "delete" && (
                    <DeleteMemberModal
                        member={member}
                        isDeleting={deleteUser.isPending}
                        onContinue={() => {
                            deleteUser.mutate(
                                { userId: member.userId },
                                {
                                    onSuccess: () => {
                                        setShowModal(undefined);
                                        toast({ title: `User ${member.fullName} has been deleted` });
                                    },
                                    onError: (e) => {
                                        setShowModal(undefined);
                                        toast({
                                            title: `Failed to delete user`,
                                            description: formatError(e),
                                        });
                                    },
                                },
                            );
                        }}
                        onClose={() => {
                            setShowModal(undefined);
                        }}
                    />
                )}
            </TableRow>
        </>
    );
};

const SuspendMemberModal: FC<{
    member: PlainOrganizationMember;
    onContinue: () => void;
    onClose: () => void;
}> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.OrganizationMemberSuspendModal}>
                <DialogHeader>
                    <DialogTitle>Suspend member</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <div className="flex flex-col gap-4">
                        <Text>Are you sure you want to suspend this member from the organization?</Text>
                        <MemberCard member={p.member} />
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={p.onClose}
                            data-tracking-id="cancel-suspend-member-suspend-member-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-suspend-member-suspend-member-modal"
                    >
                        Yes, Suspend
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const MemberCard: FC<{ member: PlainOrganizationMember } & PropsWithClassName> = (p) => {
    return (
        <Card
            className={cn(
                "inline-flex w-full items-center justify-start gap-2 rounded-xl border border-border-base bg-surface-glass px-4 py-2.5 pl-3",
                p.className,
            )}
        >
            <Avatar size={32}>
                {p.member.avatarUrl && <AvatarImage src={p.member.avatarUrl} alt={`${p.member.fullName}'s avatar`} />}
                <AvatarFallback>
                    <AvatarInitials name={p.member.fullName} size={32} />
                </AvatarFallback>
            </Avatar>
            <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center">
                <Text className="text-base font-bold">{p.member.fullName}</Text>
                <Text className="text-base text-content-secondary">{p.member.email}</Text>
            </div>
        </Card>
    );
};

const ChangeRoleModal: FC<{
    member: PlainOrganizationMember;
    onContinue: (role: OrganizationRole) => void;
    onClose: () => void;
}> = (p) => {
    const [selectedRole, setSelectedRole] = useState(roleToString(p.member.role));

    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.OrganizationMemberChangeRoleModal}>
                <DialogHeader>
                    <DialogTitle>Change role</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <MemberCard className="mb-4" member={p.member} />

                    <RadioGroup
                        value={selectedRole}
                        onValueChange={(val) => {
                            setSelectedRole(roleToString(roleFromString(val)));
                        }}
                    >
                        <div className="flex items-start justify-start gap-2 pt-1">
                            <RadioGroupItem
                                className="relative left-0 top-1 h-4 w-4"
                                value={roleToString(OrganizationRole.ADMIN)}
                                id="admin"
                            />
                            <label htmlFor="admin" className="cursor-pointer">
                                <Text className="font-bold">Admin</Text>
                                <Text>
                                    Admins can change organization settings, remove people, and manage secrets and
                                    templates
                                </Text>
                            </label>
                        </div>

                        <div className="flex items-start justify-start gap-2 pt-1">
                            <RadioGroupItem
                                className="relative left-0 top-1 h-4 w-4"
                                value={roleToString(OrganizationRole.MEMBER)}
                                id="member"
                            />
                            <label htmlFor="member" className="cursor-pointer">
                                <Text className="font-bold">Member</Text>
                                <Text>Members are standard Ona users</Text>
                            </label>
                        </div>
                    </RadioGroup>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={p.onClose}
                            data-tracking-id="cancel-change-role-change-role-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue(roleFromString(selectedRole));
                        }}
                        data-tracking-id="confirm-change-role-change-role-modal"
                    >
                        Continue
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const LeaveOrganizationModal: FC<{
    onContinue: () => void;
    onClose: () => void;
}> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.OrganizationMemberLeaveModal}>
                <DialogHeader>
                    <DialogTitle>Leave organization</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogDescription>
                    Are you sure you want to leave this organization? You will have to be re-invited to come back.
                </DialogDescription>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={p.onClose}
                            data-tracking-id="cancel-leave-organization-leave-organization-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-leave-organization-leave-organization-modal"
                    >
                        Yes, Leave
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const DeleteMemberModal: FC<{
    member: PlainOrganizationMember;
    isDeleting: boolean;
    onContinue: () => void;
    onClose: () => void;
}> = (p) => {
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.OrganizationMemberDeleteModal}>
                <DialogHeader>
                    <DialogTitle>Delete member</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody>
                    <div className="flex flex-col gap-4">
                        <Text>
                            Are you sure you want to delete this member from the organization? This action cannot be
                            undone.
                        </Text>
                        <MemberCard member={p.member} />
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={p.isDeleting}
                            onClick={p.onClose}
                            data-tracking-id="cancel-delete-member-delete-member-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        autoFocus={true}
                        variant="destructive"
                        loading={p.isDeleting}
                        onClick={(e) => {
                            e.preventDefault();
                            p.onContinue();
                        }}
                        data-tracking-id="confirm-delete-member-delete-member-modal"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

function roleToString(role: OrganizationRole): "admin" | "member" | "unspecified" {
    switch (role) {
        case OrganizationRole.ADMIN:
            return "admin";
        case OrganizationRole.MEMBER:
            return "member";
        case OrganizationRole.UNSPECIFIED:
            return "unspecified";
    }
}

function roleFromString(role: string): OrganizationRole {
    switch (role) {
        case "admin":
            return OrganizationRole.ADMIN;
        case "member":
            return OrganizationRole.MEMBER;
        default:
            return OrganizationRole.UNSPECIFIED;
    }
}
